import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { fetchAccessUsers } from '@/admin/api'
import { initials, isForbidden, moneyText, useRecords } from '@/analytics/reportKit'
import { ConfirmModal } from '@/card/record/ConfirmModal'
import { StateView } from '@/card/record/StateView'
import { Icon, type IconName } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/format'
import type { Locale } from '@/i18n'
import type { ListRow } from '@/lists/types'
import {
  approveRequest,
  createDelegation,
  fetchApprovalQueue,
  fetchDelegations,
  rejectRequest,
  revokeDelegation,
} from './api'
import type { ApprovalRequest, Delegation } from './types'
import '@/styles/work-analytics.css'

/**
 * «Согласования»: очередь на подпись и замещение (Approvals.dc.html; ТЗ §4.3, тикет #79).
 *
 * ## Очередь адресная
 *
 * Сервер отдаёт то, что ждёт этого человека, и подставить чужой идентификатор некуда. На
 * экране нет фильтра «чья очередь» — его наличие означало бы, что чужую можно посмотреть.
 *
 * ## Кнопки рисуются по `canDecide`, а не по правам раздела
 *
 * Право `system.approval` отвечает «пускать ли в согласования вообще». Может ли этот
 * человек решить именно эту строку — вопрос маршрута и делегирования, и на него отвечает
 * сервер. Кнопка по праву раздела всегда отказывала бы.
 *
 * ## Отказ требует комментария на экране, а не только на сервере
 *
 * Сервер отвергнет отказ без причины, но узнать об этом после нажатия — потерять набранное
 * решение. Поле обязательно здесь и там: это одно правило, а не две проверки.
 *
 * ## Чего на экране нет, и почему
 *
 * Вкладки «Решённые» из макета нет: сервер не отдаёт решения текущего пользователя списком
 * (есть только история одного документа). Типов и лимитов замещения тоже нет — у
 * делегирования в API только срок и основание; рисовать флажки, которые никуда не уходят,
 * значило бы обещать ограничение, которого нет.
 */

type Tab = 'queue' | 'delegation'

/** Пути карточек по типу согласуемого документа. */
const ENTITY_PATH: Record<string, string> = { quote: '/quotes', invoice: '/invoices', leave: '/leaves' }
const ENTITY_ICON: Record<string, IconName> = { quote: 'file', invoice: 'payments', leave: 'meet' }
const DAY_MS = 24 * 60 * 60 * 1000

export function ApprovalsPage() {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<Tab>('queue')
  const [notice, setNotice] = useState<string | null>(null)

  const queue = useQuery({
    queryKey: ['approval-queue'],
    queryFn: ({ signal }) => fetchApprovalQueue(signal),
    retry: false,
  })
  const delegations = useQuery({
    queryKey: ['approval-delegations'],
    queryFn: ({ signal }) => fetchDelegations(signal),
    retry: false,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['approval-queue'] })
    void queryClient.invalidateQueries({ queryKey: ['approval-delegations'] })
  }
  const failed = (error: unknown) => setNotice(error instanceof ApiError ? error.message : t('app.error'))

  const requests = queue.data?.requests ?? []
  const decidable = requests.filter((request) => request.canDecide)
  const currentDelegation = (delegations.data?.received ?? []).find((d) => d.active && !d.revokedAt)
    ?? (delegations.data?.granted ?? []).find((d) => !d.revokedAt && (d.active || d.startsOn > todayIso()))

  if (queue.error && isForbidden(queue.error)) {
    return (
      <div className="stack" style={{ gap: 14 }}>
        <div className="page-head">
          <h1 className="h1">{t('approvals.title')}</h1>
        </div>
        <StateView
          kind="forbidden"
          boxed
          title={t('approvals.forbidden.title')}
          description={t('approvals.forbidden.text')}
          actions={
            <Link className="btn btn-ghost btn-sm" to="/my-day">
              {t('approvals.toMyDay')}
            </Link>
          }
        />
      </div>
    )
  }

  return (
    <div className="stack" style={{ gap: 14 }}>
      <div className="page-head">
        <h1 className="h1">{t('approvals.title')}</h1>
        <span className="spacer" />
        {currentDelegation && (
          <button className="btn btn-ghost btn-sm" type="button" onClick={() => setTab('delegation')}>
            <Icon name="user" size="sm" />
            {t('approvals.delegation.badge', {
              name: currentDelegation.counterpartName ?? '—',
              period: periodText(currentDelegation, t),
            })}
          </button>
        )}
      </div>
      <p className="page-lead">{t('approvals.lead')}</p>

      {notice && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span className="fg">{notice}</span>
        </div>
      )}

      {queue.isLoading ? (
        <QueueSkeleton />
      ) : queue.error ? (
        <StateView
          kind="error"
          boxed
          title={t('approvals.error.title')}
          description={queue.error instanceof ApiError ? queue.error.message : t('approvals.error.text')}
          actions={
            <button className="btn btn-primary btn-sm" type="button" onClick={() => void queue.refetch()}>
              <Icon name="refresh" size="sm" />
              {t('state.retry')}
            </button>
          }
          code={queue.error instanceof ApiError ? String(queue.error.status) : undefined}
        />
      ) : (
        <>
          <div className="tabs" role="tablist" aria-label={t('approvals.title')}>
            <button className="tab" role="tab" type="button" aria-selected={tab === 'queue'} onClick={() => setTab('queue')}>
              {t('approvals.tab.queue')} <span className="count-pill">{decidable.length}</span>
            </button>
            <button
              className="tab"
              role="tab"
              type="button"
              aria-selected={tab === 'delegation'}
              onClick={() => setTab('delegation')}
            >
              {t('approvals.tab.delegation')}
            </button>
          </div>

          {tab === 'queue' ? (
            <Queue
              requests={requests}
              onDone={() => {
                setNotice(null)
                refresh()
              }}
              onError={failed}
              onOpenDelegation={() => setTab('delegation')}
            />
          ) : (
            <DelegationTab
              granted={delegations.data?.granted ?? []}
              received={delegations.data?.received ?? []}
              isLoading={delegations.isLoading}
              error={delegations.error}
              mayEdit={can(user, 'system.approval', 'update')}
              onDone={() => {
                setNotice(null)
                refresh()
              }}
              onError={failed}
            />
          )}
        </>
      )}
    </div>
  )
}

// ------------------------------------------------------------------ очередь

/** Сведения о документе из механизма списков — номер, клиент, сумма. */
interface DocumentInfo {
  number: string | null
  party: string | null
  amount: string | null
}

function useDocumentInfo(requests: ApprovalRequest[]): Map<string, DocumentInfo> {
  const { user } = useApp()
  const idsOf = (type: string) => requests.filter((r) => r.entityType === type).map((r) => r.entityId)
  // Запрос идёт, только если у смотрящего есть право на список: без него номер и сумма
  // документа ему не положены, и строка очереди остаётся с видом документа.
  const quotes = useRecords(can(user, 'sales.quote', 'read') ? 'sales.quote' : null, ['number', 'counterparty_id', 'total', 'currency'], idsOf('quote'))
  const invoices = useRecords(can(user, 'billing.invoice', 'read') ? 'billing.invoice' : null, ['number', 'counterparty_id', 'total', 'currency'], idsOf('invoice'))
  const leaves = useRecords(can(user, 'hr.leave', 'read') ? 'hr.leave' : null, ['employee_id', 'start_date', 'end_date', 'days_count'], idsOf('leave'))
  const { locale, t } = useApp()

  return useMemo(() => {
    const info = new Map<string, DocumentInfo>()
    const money = (row: ListRow) => moneyText(row.total as string | null, (row.currency as string | null) ?? null, locale)
    for (const [id, row] of quotes.rows) {
      info.set(id, { number: str(row.number), party: str(row.counterparty_id__label), amount: money(row) })
    }
    for (const [id, row] of invoices.rows) {
      info.set(id, { number: str(row.number), party: str(row.counterparty_id__label), amount: money(row) })
    }
    for (const [id, row] of leaves.rows) {
      const from = str(row.start_date)
      const to = str(row.end_date)
      info.set(id, {
        number: from && to ? `${formatDate(from, locale)} – ${formatDate(to, locale)}` : null,
        party: str(row.employee_id__label),
        amount: row.days_count != null ? t('approvals.leaveDays', { days: String(row.days_count) }) : null,
      })
    }
    return info
  }, [quotes.rows, invoices.rows, leaves.rows, locale, t])
}

function Queue({
  requests,
  onDone,
  onError,
  onOpenDelegation,
}: {
  requests: ApprovalRequest[]
  onDone: () => void
  onError: (error: unknown) => void
  onOpenDelegation: () => void
}) {
  const { t, locale } = useApp()
  const info = useDocumentInfo(requests)
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState<string | null>(null)
  const [longOnly, setLongOnly] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [openId, setOpenId] = useState<string | null>(null)
  const [comment, setComment] = useState('')
  const [touched, setTouched] = useState(false)

  const decide = useMutation({
    mutationFn: ({ id, approve, text }: { id: string; approve: boolean; text: string }) =>
      approve ? approveRequest(id, text) : rejectRequest(id, text),
    onSuccess: () => {
      setOpenId(null)
      setComment('')
      setTouched(false)
      onDone()
    },
    onError,
  })

  const bulk = useMutation({
    // По одному запросу на строку: у сервера нет массового решения, и отказ по одной строке
    // не должен отменять уже принятые по остальным.
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await approveRequest(id, '')
    },
    onSuccess: () => {
      setSelected(new Set())
      onDone()
    },
    onError: (error: unknown) => {
      setSelected(new Set())
      onError(error)
      onDone()
    },
  })

  const now = Date.now()
  const waited = (request: ApprovalRequest) => now - Date.parse(request.requestedAt)
  const kinds = Array.from(new Set(requests.map((r) => r.entityType)))
  const needle = search.trim().toLowerCase()
  const visible = requests
    .filter((r) => !kind || r.entityType === kind)
    .filter((r) => !longOnly || waited(r) > DAY_MS)
    .filter((r) => {
      if (!needle) return true
      const doc = info.get(r.entityId)
      return [t(`approvals.entity.${r.entityType}`), r.requestedByName, doc?.number, doc?.party]
        .some((part) => part?.toLowerCase().includes(needle))
    })
    .sort((a, b) => Date.parse(a.requestedAt) - Date.parse(b.requestedAt))
  const busy = decide.isPending || bulk.isPending

  if (requests.length === 0) {
    return (
      <StateView
        kind="empty"
        boxed
        title={t('approvals.queue.empty')}
        description={t('approvals.queue.emptyText')}
        actions={
          <button className="btn btn-ghost btn-sm" type="button" onClick={onOpenDelegation}>
            {t('approvals.queue.setupDelegation')}
          </button>
        }
      />
    )
  }

  const selectable = visible.filter((r) => r.canDecide)
  const allSelected = selectable.length > 0 && selectable.every((r) => selected.has(r.id))
  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const openReject = (id: string) => {
    setOpenId(openId === id ? null : id)
    setComment('')
    setTouched(false)
  }

  return (
    <>
      <div className="toolbar">
        <div className="input-icon wa-search">
          <Icon name="search" />
          <label htmlFor="aq" className="sr-only">{t('approvals.search')}</label>
          <input
            id="aq"
            className="input input-sm"
            placeholder={t('approvals.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="vsep" />
        {kinds.map((code) => (
          <button
            key={code}
            className="chip"
            type="button"
            aria-pressed={kind === code}
            onClick={() => setKind(kind === code ? null : code)}
          >
            {t(`approvals.entity.${code}`)} <span className="count">{requests.filter((r) => r.entityType === code).length}</span>
          </button>
        ))}
        <button className="chip" type="button" aria-pressed={longOnly} onClick={() => setLongOnly(!longOnly)}>
          <Icon name="clock" />
          {t('approvals.chip.long')} <span className="count">{requests.filter((r) => waited(r) > DAY_MS).length}</span>
        </button>
      </div>

      {selected.size > 0 && (
        <div className="bulk" role="toolbar" aria-label={t('approvals.bulk.label')}>
          <span className="sel">
            <input type="checkbox" checked aria-label={t('approvals.bulk.clear')} onChange={() => setSelected(new Set())} />
            {t('approvals.bulk.selected', { count: selected.size })}
          </span>
          <button className="btn btn-secondary btn-sm" type="button" disabled={busy} onClick={() => bulk.mutate(Array.from(selected))}>
            <Icon name="check" size="sm" />
            {t('approvals.bulk.approve', { count: selected.size })}
          </button>
          <button className="btn btn-ghost btn-sm" type="button" onClick={() => setSelected(new Set())}>
            {t('approvals.bulk.clear')}
          </button>
          <span className="spacer" />
          <span className="t-sm secondary">{t('approvals.bulk.hint')}</span>
        </div>
      )}

      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th className="chk">
                <input
                  type="checkbox"
                  aria-label={t('approvals.selectAll')}
                  checked={allSelected}
                  disabled={selectable.length === 0}
                  onChange={() => setSelected(allSelected ? new Set() : new Set(selectable.map((r) => r.id)))}
                />
              </th>
              <th>{t('approvals.col.document')}</th>
              <th>{t('approvals.requestedBy')}</th>
              <th className="num">{t('approvals.col.amount')}</th>
              <th>{t('approvals.col.waits')}</th>
              <th>{t('approvals.col.step')}</th>
              <th className="num">{t('approvals.col.decision')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((request) => {
              const doc = info.get(request.entityId)
              const base = ENTITY_PATH[request.entityType]
              const label = t(`approvals.entity.${request.entityType}`)
              const isOpen = openId === request.id
              const long = waited(request) > DAY_MS
              const previous = request.steps
                .filter((step) => step.stepOrder < request.currentStep && step.status === 'approved')
                .at(-1)
              const commentMissing = !comment.trim()
              return (
                <Fragment key={request.id}>
                  <tr className={isOpen ? 'is-open' : selected.has(request.id) ? 'is-selected' : undefined}>
                    <td className="chk">
                      {request.canDecide && (
                        <input
                          type="checkbox"
                          checked={selected.has(request.id)}
                          aria-label={t('approvals.select', { name: doc?.number ?? label })}
                          onChange={() => toggle(request.id)}
                        />
                      )}
                    </td>
                    <td className="wrap" style={{ maxWidth: 440 }}>
                      <span className="stack" style={{ gap: 1 }}>
                        <span className="row t-xs muted" style={{ gap: 4 }}>
                          <Icon name={ENTITY_ICON[request.entityType] ?? 'file'} size="xs" />
                          {label}
                        </span>
                        {base ? (
                          <Link className="strong" to={`${base}/${request.entityId}`}>
                            {doc?.number ?? label}
                          </Link>
                        ) : (
                          <span className="strong">{doc?.number ?? label}</span>
                        )}
                        {doc?.party && <span className="t-sm secondary">{doc.party}</span>}
                      </span>
                    </td>
                    <td>
                      <span className="row" style={{ gap: 8 }}>
                        <span className="ava">{initials(request.requestedByName)}</span>
                        {request.requestedByName ?? '—'}
                      </span>
                    </td>
                    <td className="num">{doc?.amount ?? <span className="muted">—</span>}</td>
                    <td>
                      <span className="stack" style={{ gap: 0 }}>
                        {long ? (
                          <span className="flag flag-warning">
                            <Icon name="clock" />
                            {t('approvals.waitLong', { time: waitText(waited(request), t) })}
                          </span>
                        ) : (
                          <span>{waitText(waited(request), t)}</span>
                        )}
                        <span className="t-xs muted">{t('approvals.since', { at: moment(request.requestedAt, locale) })}</span>
                      </span>
                    </td>
                    <td>
                      <span className="stack" style={{ gap: 0 }}>
                        <span>{t('approvals.stepOf', { step: request.currentStep, total: request.steps.length })}</span>
                        {previous?.approverName && (
                          <span className="t-xs muted">{t('approvals.previousStep', { name: previous.approverName })}</span>
                        )}
                      </span>
                    </td>
                    <td className="num">
                      {/* Кнопки — по canDecide: право пускает в согласования, а решает маршрут. */}
                      {request.canDecide ? (
                        <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            className="btn btn-danger-quiet btn-sm"
                            type="button"
                            aria-expanded={isOpen}
                            disabled={busy}
                            onClick={() => openReject(request.id)}
                          >
                            {t('approvals.rejectOpen')}
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            type="button"
                            disabled={busy}
                            onClick={() => decide.mutate({ id: request.id, approve: true, text: '' })}
                          >
                            <Icon name="check" size="sm" />
                            {t('approvals.approve')}
                          </button>
                        </span>
                      ) : (
                        <span className="t-sm muted">{t('approvals.waitingOnSomeoneElse')}</span>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="detail">
                      <td colSpan={7}>
                        <div className="row" style={{ alignItems: 'flex-start', gap: 28, flexWrap: 'wrap' }}>
                          <div className="stack" style={{ gap: 10, flex: '1 1 440px', maxWidth: 620 }}>
                            <span className="h3">{t('approvals.decideTitle', { name: doc?.number ?? label })}</span>
                            <div className="field">
                              <label className="label" htmlFor={`rj-${request.id}`}>
                                {t('approvals.comment')} <span className="req" aria-hidden="true">*</span>
                              </label>
                              <textarea
                                id={`rj-${request.id}`}
                                className={touched && commentMissing ? 'input is-invalid' : 'input'}
                                aria-invalid={touched && commentMissing}
                                aria-describedby={`rj-e-${request.id}`}
                                placeholder={t('approvals.commentPlaceholder')}
                                value={comment}
                                autoFocus
                                onChange={(event) => setComment(event.target.value)}
                                onBlur={() => setTouched(true)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Escape') openReject(request.id)
                                  if (event.key === 'Enter' && event.ctrlKey && !commentMissing) {
                                    decide.mutate({ id: request.id, approve: false, text: comment.trim() })
                                  }
                                }}
                              />
                              {touched && commentMissing ? (
                                <span id={`rj-e-${request.id}`} className="err-text">
                                  <Icon name="alert" size="sm" />
                                  {t('approvals.needCommentFor', { name: request.requestedByName ?? '—' })}
                                </span>
                              ) : (
                                <span id={`rj-e-${request.id}`} className="help">{t('approvals.commentHelp')}</span>
                              )}
                            </div>
                            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                              <button className="btn btn-secondary btn-sm" type="button" onClick={() => openReject(request.id)}>
                                {t('wa.cancel')}
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                type="button"
                                /* Отказ без причины отработать нельзя: тому, кто просил,
                                   неизвестно, что менять перед повторной попыткой. */
                                disabled={busy || commentMissing}
                                title={commentMissing ? t('approvals.needComment') : undefined}
                                onClick={() => decide.mutate({ id: request.id, approve: false, text: comment.trim() })}
                              >
                                {t('approvals.reject')}
                              </button>
                              <button
                                className="btn btn-ghost btn-sm"
                                type="button"
                                disabled={busy}
                                onClick={() => decide.mutate({ id: request.id, approve: true, text: comment.trim() })}
                              >
                                {t('approvals.approveWithComment')}
                              </button>
                              <span className="t-xs muted">{t('approvals.keys')}</span>
                            </div>
                          </div>
                          <dl className="kv" style={{ flex: '0 1 420px', gridTemplateColumns: '128px minmax(0, 1fr)' }}>
                            {request.steps.map((step) => (
                              <Fragment key={step.id}>
                                <dt>{t('approvals.stepOf', { step: step.stepOrder, total: request.steps.length })}</dt>
                                <dd>
                                  <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                                    <StepBadge status={step.status} />
                                    <span className="t-sm secondary">
                                      {step.approverName ?? t('approvals.byRole')}
                                      {step.decidedAt ? `, ${moment(step.decidedAt, locale)}` : ''}
                                    </span>
                                  </span>
                                  {step.comment && <span className="t-sm secondary">«{step.comment}»</span>}
                                </dd>
                              </Fragment>
                            ))}
                          </dl>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      {visible.length === 0 && <p className="t-sm muted">{t('approvals.queue.noMatch')}</p>}
      <p className="t-sm muted">{t('approvals.queue.footer')}</p>
    </>
  )
}

function StepBadge({ status }: { status: string }) {
  const { t } = useApp()
  const view: Record<string, [string, IconName]> = {
    approved: ['badge b-success', 'check'],
    rejected: ['badge b-danger', 'close'],
    pending: ['badge b-info', 'clock'],
    skipped: ['badge', 'arrow-right'],
  }
  const [cls, icon] = view[status] ?? ['badge', 'clock']
  return (
    <span className={cls}>
      <Icon name={icon} />
      {t(`approvals.stepStatus.${status}`)}
    </span>
  )
}

function QueueSkeleton() {
  const { t } = useApp()
  return (
    <div className="tbl-wrap" aria-busy="true" aria-label={t('approvals.loading')}>
      <table className="tbl">
        <tbody>
          {Array.from({ length: 6 }, (_, index) => (
            <tr key={index}>
              <td style={{ height: 52 }}>
                <span className="stack" style={{ gap: 6 }}>
                  <span className="skel" style={{ width: 90, height: 8 }} />
                  <span className="skel" style={{ width: `${44 + ((index * 13) % 26)}%` }} />
                </span>
              </td>
              <td><span className="skel" style={{ width: 110 }} /></td>
              <td><span className="skel" style={{ width: 100 }} /></td>
              <td><span className="skel" style={{ width: 56 }} /></td>
              <td><span className="skel" style={{ width: 184, height: 26 }} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ------------------------------------------------------------------ замещение

/**
 * Делегирования в обе стороны.
 *
 * Показываются оба списка. Без «чью подпись я несу» непонятно, почему в очереди чужие
 * документы, а без «кому передал» — почему свои перестали приходить. Отозванное остаётся:
 * «кто подписывал в феврале» спрашивают через год.
 */
function DelegationTab({
  granted,
  received,
  isLoading,
  error,
  mayEdit,
  onDone,
  onError,
}: {
  granted: Delegation[]
  received: Delegation[]
  isLoading: boolean
  error: unknown
  mayEdit: boolean
  onDone: () => void
  onError: (error: unknown) => void
}) {
  const { t, locale } = useApp()
  const [revoking, setRevoking] = useState<Delegation | null>(null)
  const revoke = useMutation({
    mutationFn: (id: string) => revokeDelegation(id),
    onSuccess: () => {
      setRevoking(null)
      onDone()
    },
    onError: (failure: unknown) => {
      setRevoking(null)
      onError(failure)
    },
  })

  if (isLoading) return <div className="panel"><div className="panel-b"><span className="skel" style={{ width: '60%' }} /></div></div>
  if (error) {
    return (
      <StateView
        kind={isForbidden(error) ? 'forbidden' : 'error'}
        boxed
        title={t('approvals.delegation.error')}
        description={error instanceof ApiError ? error.message : undefined}
      />
    )
  }

  const current = granted.filter((d) => !d.revokedAt && (d.active || d.startsOn > todayIso()))
  const past = granted.filter((d) => !current.includes(d))

  return (
    <div className="layout-2-wide">
      <div className="stack" style={{ gap: 14 }}>
        <section className="panel" aria-labelledby="h-cur">
          <div className="panel-h">
            <span className="h2" id="h-cur">{t('approvals.delegation.granted')}</span>
          </div>
          {current.length === 0 ? (
            <div className="panel-b secondary">{t('approvals.delegation.none')}</div>
          ) : (
            current.map((delegation) => (
              <div key={delegation.id} className="list-item" style={{ gap: 12, flexWrap: 'wrap' }}>
                <span className="ava ava-lg">{initials(delegation.counterpartName)}</span>
                <div className="stack" style={{ flex: '1 1 240px', gap: 0 }}>
                  <span className="strong">{delegation.counterpartName ?? '—'}</span>
                  <span className="t-sm secondary">
                    {periodText(delegation, t, locale)}
                    {delegation.reason ? ` · ${delegation.reason}` : ''}
                  </span>
                </div>
                <DelegationState delegation={delegation} />
                {mayEdit && (
                  <button className="btn btn-danger-quiet btn-sm" type="button" onClick={() => setRevoking(delegation)}>
                    {t('approvals.delegation.revokeAsk')}
                  </button>
                )}
              </div>
            ))
          )}
        </section>

        {mayEdit && <NewDelegation onDone={onDone} onError={onError} />}
      </div>

      <aside className="stack" style={{ gap: 14 }}>
        <section className="panel" aria-labelledby="h-rec">
          <div className="panel-h">
            <span className="h2" id="h-rec">{t('approvals.delegation.received')}</span>
          </div>
          {received.length === 0 ? (
            <div className="panel-b secondary">{t('approvals.delegation.noneReceived')}</div>
          ) : (
            received.map((delegation) => (
              <div key={delegation.id} className="list-item">
                <div className="stack" style={{ flex: 1, gap: 0 }}>
                  <span>{delegation.counterpartName ?? '—'} · {periodText(delegation, t, locale)}</span>
                  {delegation.reason && <span className="t-xs muted">{delegation.reason}</span>}
                </div>
                <DelegationState delegation={delegation} />
              </div>
            ))
          )}
        </section>
        <section className="panel" aria-labelledby="h-hist">
          <div className="panel-h">
            <span className="h2" id="h-hist">{t('approvals.delegation.past')}</span>
          </div>
          {past.length === 0 ? (
            <div className="panel-b secondary">{t('approvals.delegation.noPast')}</div>
          ) : (
            past.map((delegation) => (
              <div key={delegation.id} className="list-item">
                <div className="stack" style={{ flex: 1, gap: 0 }}>
                  <span>{delegation.counterpartName ?? '—'} · {periodText(delegation, t, locale)}</span>
                  {delegation.reason && <span className="t-xs muted">{delegation.reason}</span>}
                </div>
                <DelegationState delegation={delegation} />
              </div>
            ))
          )}
        </section>
        <div className="alert alert-neutral" style={{ alignItems: 'flex-start' }}>
          <Icon name="alert" />
          <span>{t('approvals.delegation.thresholdsNote')}</span>
        </div>
      </aside>

      <ConfirmModal
        open={revoking != null}
        title={t('approvals.delegation.revokeTitle', { name: revoking?.counterpartName ?? '—' })}
        confirmLabel={t('approvals.delegation.revoke')}
        icon="close"
        pending={revoke.isPending}
        onCancel={() => setRevoking(null)}
        onConfirm={() => revoking && revoke.mutate(revoking.id)}
      >
        {t('approvals.delegation.revokeText')}
      </ConfirmModal>
    </div>
  )
}

function DelegationState({ delegation }: { delegation: Delegation }) {
  const { t } = useApp()
  // Отозванное показывается, а не прячется: ответ «кто подписывал» обязан пережить возврат права.
  if (delegation.revokedAt) return <span className="badge b-outline">{t('approvals.delegation.revoked')}</span>
  if (delegation.active)
    return (
      <span className="badge b-success">
        <Icon name="check" />
        {t('approvals.delegation.active')}
      </span>
    )
  if (delegation.startsOn > todayIso())
    return (
      <span className="badge b-info">
        <Icon name="clock" />
        {t('approvals.delegation.planned')}
      </span>
    )
  return <span className="badge">{t('approvals.delegation.expired')}</span>
}

/**
 * Новое замещение.
 *
 * Заместителя выбирают из справочника пользователей: делегат — учётная запись, а не
 * сотрудник (согласование совершает учётная запись). Справочник закрыт правом
 * `system.user`; у кого его нет, тому выбрать некого, и это сказано словами.
 */
function NewDelegation({ onDone, onError }: { onDone: () => void; onError: (error: unknown) => void }) {
  const { t, user } = useApp()
  const mayPick = can(user, 'system.user', 'read')
  const [query, setQuery] = useState('')
  const [delegate, setDelegate] = useState<{ id: string; name: string } | null>(null)
  const [from, setFrom] = useState(todayIso())
  const [to, setTo] = useState('')
  const [reason, setReason] = useState('')

  const found = useQuery({
    queryKey: ['delegate-search', query],
    enabled: mayPick && delegate == null && query.trim().length >= 2,
    queryFn: ({ signal }) => fetchAccessUsers(query.trim(), signal),
    retry: false,
  })

  const save = useMutation({
    mutationFn: () => createDelegation({ delegateId: delegate!.id, startsOn: from || null, endsOn: to || null, reason: reason.trim() || null }),
    onSuccess: () => {
      setDelegate(null)
      setQuery('')
      setTo('')
      setReason('')
      onDone()
    },
    onError,
  })

  const badRange = Boolean(from && to && to < from)

  return (
    <section className="panel" aria-labelledby="h-new">
      <div className="panel-h">
        <span className="h2" id="h-new">{t('approvals.delegation.new')}</span>
      </div>
      <div className="panel-b stack" style={{ gap: 16 }}>
        {!mayPick ? (
          <StateView kind="setup" compact title={t('approvals.delegation.noDirectory')} description={t('approvals.delegation.noDirectoryText')} />
        ) : (
          <div className="form-grid">
            <div className="field span-2">
              <label className="label" htmlFor="dz-who">
                {t('approvals.delegation.to')} <span className="req" aria-hidden="true">*</span>
              </label>
              {delegate ? (
                <div className="input">
                  <span className="ava">{initials(delegate.name)}</span>
                  <span className="ellipsis">{delegate.name}</span>
                  <span className="spacer" />
                  <button className="btn btn-ghost btn-sm btn-icon" type="button" aria-label={t('picker.clear')} onClick={() => setDelegate(null)}>
                    <Icon name="close" size="sm" />
                  </button>
                </div>
              ) : (
                <>
                  <input id="dz-who" className="input" value={query} placeholder={t('picker.search')} onChange={(event) => setQuery(event.target.value)} />
                  {(found.data ?? [])
                    .filter((candidate) => candidate.active && candidate.id !== user?.userId)
                    .slice(0, 8)
                    .map((candidate) => (
                      <button
                        key={candidate.id}
                        type="button"
                        className="menu-item"
                        onClick={() => setDelegate({ id: candidate.id, name: candidate.fullName })}
                      >
                        <span className="ava">{initials(candidate.fullName)}</span>
                        {candidate.fullName}
                        <span className="t-xs muted">{candidate.username}</span>
                      </button>
                    ))}
                </>
              )}
              <span className="help">{t('approvals.delegation.toHelp')}</span>
            </div>
            <div className="field">
              <label className="label" htmlFor="dz-from">{t('approvals.delegation.from')}</label>
              <input id="dz-from" className="input" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
            </div>
            <div className="field">
              <label className="label" htmlFor="dz-to">{t('approvals.delegation.until')}</label>
              <input
                id="dz-to"
                className={badRange ? 'input is-invalid' : 'input'}
                type="date"
                value={to}
                aria-invalid={badRange}
                onChange={(event) => setTo(event.target.value)}
              />
              <span className={badRange ? 'err-text' : 'help'}>
                {badRange ? t('approvals.delegation.badRange') : t('approvals.delegation.untilHelp')}
              </span>
            </div>
            <div className="field span-2">
              <label className="label" htmlFor="dz-why">{t('approvals.delegation.reason')}</label>
              <input id="dz-why" className="input" value={reason} onChange={(event) => setReason(event.target.value)} />
            </div>
          </div>
        )}
        <div className="alert alert-info">
          <Icon name="alert" />
          <span className="fg">{t('approvals.delegation.info')}</span>
        </div>
      </div>
      {mayPick && (
        <div className="panel-f" style={{ justifyContent: 'flex-end' }}>
          <span className="muted" style={{ marginRight: 'auto' }}>{t('approvals.delegation.notify')}</span>
          <button
            className="btn btn-primary"
            type="button"
            disabled={!delegate || badRange || save.isPending}
            onClick={() => save.mutate()}
          >
            {t('approvals.delegation.save')}
          </button>
        </div>
      )}
    </section>
  )
}

// ------------------------------------------------------------------ мелочи

function str(value: unknown): string | null {
  return value == null || value === undefined || value === '' ? null : String(value)
}

function todayIso(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

function periodText(
  delegation: Delegation,
  t: (key: string, p?: Record<string, string | number>) => string,
  locale: Locale = 'ru',
): string {
  const from = formatDate(delegation.startsOn, locale)
  return delegation.endsOn
    ? `${from} – ${formatDate(delegation.endsOn, locale)}`
    : t('approvals.delegation.fromOpen', { from })
}

/** «3 ч», «2 дн.»: сколько документ ждёт решения. */
function waitText(ms: number, t: (key: string, p?: Record<string, string | number>) => string): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000))
  if (minutes < 60) return t('approvals.wait.minutes', { count: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('approvals.wait.hours', { count: hours })
  return t('approvals.wait.days', { count: Math.floor(hours / 24) })
}

function moment(value: string, locale: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
